import { useEffect, useState } from "react";
import { Calendar, Plus, Trash2, Clock, X } from "lucide-react";
import api, { fmtDateUS } from "@/lib/api";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

/**
 * LeadAppointments — schedule + manage appointments for a single lead.
 *
 * Writes to /api/leads/{id}/appointments → backend persists in `events` collection
 * with source="lead" so they automatically appear on the Calendar page.
 */

function fmtTime12(time24) {
  if (!time24) return "";
  const [h, m] = time24.split(":").map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function LeadAppointments({ leadId, leadName, onChanged }) {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", date: todayISO(), time: "10:00", duration_minutes: 30, notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/leads/${leadId}/appointments`);
      setAppointments(data);
    } catch (e) {
      // empty timeline is fine
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [leadId]);

  const resetForm = () => {
    setForm({ title: "", date: todayISO(), time: "10:00", duration_minutes: 30, notes: "" });
    setAdding(false);
  };

  const submit = async () => {
    if (!form.date) { toast.error("Pick a date"); return; }
    setSubmitting(true);
    try {
      const body = {
        title: form.title.trim() || null,
        date: form.date,
        time: form.time || null,
        duration_minutes: Number(form.duration_minutes) || 30,
        notes: form.notes.trim() || null,
      };
      await api.post(`/leads/${leadId}/appointments`, body);
      toast.success("Appointment added · also on the calendar");
      resetForm();
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/leads/${leadId}/appointments/${confirmDelete.id}`);
      toast.success("Appointment removed");
      setConfirmDelete(null);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove");
    }
  };

  return (
    <div data-testid="lead-appointments">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar size={14} style={{ color: "var(--lime)" }} />
          <span className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--text)" }}>
            Appointments
          </span>
        </div>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition"
            style={{ background: "var(--lime)", color: "#0c0c0c" }}
            data-testid="appointment-add"
          >
            <Plus size={12} /> Add Appointment
          </button>
        )}
      </div>

      {adding && (
        <div
          className="rounded-2xl p-4 mb-4 space-y-3"
          style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}
          data-testid="appointment-form"
        >
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={`Meet with ${leadName || "lead"} (optional)`}
            className="input-base text-sm"
            data-testid="appointment-title"
          />
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="input-base text-sm"
                data-testid="appointment-date"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Time</label>
              <input
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                className="input-base text-sm"
                data-testid="appointment-time"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Duration</label>
              <select
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })}
                className="input-base text-sm"
                data-testid="appointment-duration"
              >
                {[15, 30, 45, 60, 90, 120].map((m) => <option key={m} value={m}>{m} min</option>)}
              </select>
            </div>
          </div>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
            placeholder="Notes (optional)"
            className="input-base text-sm"
            data-testid="appointment-notes"
          />
          <div className="flex items-center justify-end gap-2">
            <button onClick={resetForm} className="text-xs px-3 py-1.5 rounded-xl" style={{ color: "var(--text-muted)" }}>
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting}
              className="text-xs font-semibold px-4 py-1.5 rounded-xl flex items-center gap-1.5 disabled:opacity-50"
              style={{ background: "var(--lime)", color: "#0c0c0c" }}
              data-testid="appointment-save"
            >
              {submitting ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-4"><span className="spinner" /></div>
      ) : appointments.length === 0 ? (
        !adding && (
          <div className="text-center py-4 text-xs" style={{ color: "var(--text-dim)" }}>
            No appointments scheduled — appointments you add here show up on the calendar.
          </div>
        )
      ) : (
        <ul className="space-y-2">
          {appointments.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-3 p-3 rounded-xl"
              style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
              data-testid={`appointment-${a.id}`}
            >
              <div
                className="w-10 h-10 rounded-xl flex flex-col items-center justify-center shrink-0"
                style={{ background: "var(--lime-glow)" }}
              >
                <Calendar size={11} style={{ color: "var(--lime)" }} />
                <span className="font-mono text-[9px] mt-0.5" style={{ color: "var(--lime)" }}>
                  {a.date.slice(5).replace("-", "/")}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{a.title}</div>
                <div className="text-xs flex items-center gap-2 mt-0.5" style={{ color: "var(--text-muted)" }}>
                  <span>{fmtDateUS(a.date)}</span>
                  {a.time && (
                    <>
                      <span>·</span>
                      <Clock size={10} />
                      <span>{fmtTime12(a.time)}</span>
                      {a.duration_minutes ? <span>· {a.duration_minutes}m</span> : null}
                    </>
                  )}
                </div>
                {a.notes && (
                  <div className="text-xs mt-1 whitespace-pre-wrap" style={{ color: "var(--text-muted)" }}>
                    {a.notes}
                  </div>
                )}
              </div>
              <button
                onClick={() => setConfirmDelete(a)}
                className="p-1.5 rounded-xl hover:bg-white/5 transition shrink-0"
                style={{ color: "#f87171" }}
                title="Remove appointment"
                data-testid={`appointment-delete-${a.id}`}
              >
                <Trash2 size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Remove appointment?"
        description={`This will also remove it from your calendar. ${confirmDelete?.title || ""}`}
        destructive
        confirmLabel="Remove"
        onCancel={() => setConfirmDelete(null)}
        onConfirm={remove}
      />
    </div>
  );
}
